import { useState } from 'react';
import {
  MeasurementReading,
  TemplateMeasurementConfig,
  TrendAnalysis,
} from '@easydone/shared';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

interface MeasurementChartProps {
  readings: MeasurementReading[];
  definition?: TemplateMeasurementConfig;
  trend?: TrendAnalysis;
  height?: number;
}

export function MeasurementChart({
  readings,
  definition,
  trend,
  height = 300,
}: MeasurementChartProps) {
  const [chartType, setChartType] = useState<'line' | 'bar'>(
    definition?.chartType === 'bar' ? 'bar' : 'line'
  );

  // Filter to numeric readings only
  const numericReadings = readings.filter((r) => typeof r.value === 'number');

  if (numericReadings.length === 0) {
    return (
      <div
        className="flex items-center justify-center bg-gray-50 rounded-lg"
        style={{ height }}
      >
        <div className="text-center">
          <ChartBarIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">No data available</p>
        </div>
      </div>
    );
  }

  // Calculate chart dimensions and scales
  const values = numericReadings.map((r) => r.value as number);
  const maxValue = Math.max(...values);
  const minValue = Math.min(...values);
  const range = maxValue - minValue || 1;

  const padding = { top: 20, right: 20, bottom: 40, left: 60 };
  const chartWidth = 800;
  const chartHeight = height - padding.top - padding.bottom;

  // Calculate data points
  const dataPoints = numericReadings.map((reading, index) => {
    const x = padding.left + (index * (chartWidth - padding.left - padding.right)) / (numericReadings.length - 1 || 1);
    const value = reading.value as number;
    const y = padding.top + chartHeight - ((value - minValue) / range) * chartHeight;

    return { x, y, value, reading, index };
  });

  // Generate path for line chart
  const linePath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  // Generate threshold lines
  const thresholdLines: Array<{ y: number; color: string; label: string; value: number }> = [];

  if (definition?.warningThreshold?.max !== undefined) {
    const y = padding.top + chartHeight - ((definition.warningThreshold.max - minValue) / range) * chartHeight;
    thresholdLines.push({
      y,
      color: '#f59e0b',
      label: 'Warning Max',
      value: definition.warningThreshold.max,
    });
  }

  if (definition?.criticalThreshold?.max !== undefined) {
    const y = padding.top + chartHeight - ((definition.criticalThreshold.max - minValue) / range) * chartHeight;
    thresholdLines.push({
      y,
      color: '#ef4444',
      label: 'Critical Max',
      value: definition.criticalThreshold.max,
    });
  }

  // Format value with precision
  const formatValue = (value: number) => {
    if (definition?.precision !== undefined) {
      return value.toFixed(definition.precision);
    }
    return value.toFixed(2);
  };

  // Format date
  const formatDate = (timestamp: any) => {
    let date: Date;
    if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else if (timestamp && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    } else if (timestamp && typeof timestamp.seconds === 'number') {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date();
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Render trend indicator
  const renderTrendIndicator = () => {
    if (!trend) return null;

    const getTrendIcon = () => {
      switch (trend.trend.direction) {
        case 'increasing':
          return <ArrowTrendingUpIcon className="w-5 h-5" />;
        case 'decreasing':
          return <ArrowTrendingDownIcon className="w-5 h-5" />;
        case 'stable':
          return <MinusIcon className="w-5 h-5" />;
        default:
          return null;
      }
    };

    const getTrendColor = () => {
      if (trend.trend.direction === 'stable') return 'text-gray-600';
      return 'text-blue-600';
    };

    return (
      <div className={`flex items-center gap-2 text-sm ${getTrendColor()}`}>
        {getTrendIcon()}
        <span className="font-medium capitalize">{trend.trend.direction}</span>
        {trend.trend.changePercent !== 0 && (
          <span>
            ({trend.trend.changePercent > 0 ? '+' : ''}
            {trend.trend.changePercent.toFixed(1)}%)
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Chart Header */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-gray-900">
            {definition?.name || 'Measurement Trend'}
          </h4>
          {renderTrendIndicator()}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setChartType('line')}
            className={`px-3 py-1 text-sm rounded ${
              chartType === 'line'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Line
          </button>
          <button
            onClick={() => setChartType('bar')}
            className={`px-3 py-1 text-sm rounded ${
              chartType === 'bar'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Bar
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <svg width="100%" height={height} viewBox={`0 0 ${chartWidth} ${height}`}>
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
            const y = padding.top + chartHeight * (1 - fraction);
            const value = minValue + range * fraction;
            return (
              <g key={fraction}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={chartWidth - padding.right}
                  y2={y}
                  stroke="#e5e7eb"
                  strokeWidth="1"
                />
                <text
                  x={padding.left - 10}
                  y={y + 4}
                  textAnchor="end"
                  className="text-xs fill-gray-600"
                >
                  {formatValue(value)}
                  {definition?.unit && ` ${definition.unit}`}
                </text>
              </g>
            );
          })}

          {/* Threshold lines */}
          {thresholdLines.map((threshold, i) => (
            <g key={i}>
              <line
                x1={padding.left}
                y1={threshold.y}
                x2={chartWidth - padding.right}
                y2={threshold.y}
                stroke={threshold.color}
                strokeWidth="2"
                strokeDasharray="4 4"
              />
              <text
                x={chartWidth - padding.right - 5}
                y={threshold.y - 5}
                textAnchor="end"
                className="text-xs font-medium"
                fill={threshold.color}
              >
                {threshold.label}: {formatValue(threshold.value)}
              </text>
            </g>
          ))}

          {/* Chart content */}
          {chartType === 'line' ? (
            <>
              {/* Line */}
              <path
                d={linePath}
                fill="none"
                stroke="#3b82f6"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* Data points */}
              {dataPoints.map((point, i) => (
                <g key={i}>
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r="4"
                    fill={
                      point.reading.status === 'critical'
                        ? '#ef4444'
                        : point.reading.status === 'warning'
                        ? '#f59e0b'
                        : '#3b82f6'
                    }
                    stroke="white"
                    strokeWidth="2"
                  >
                    <title>
                      {formatDate(point.reading.readingDate)}: {formatValue(point.value)}
                      {definition?.unit && ` ${definition.unit}`}
                    </title>
                  </circle>
                </g>
              ))}
            </>
          ) : (
            <>
              {/* Bars */}
              {dataPoints.map((point, i) => {
                const barWidth = Math.max(
                  10,
                  (chartWidth - padding.left - padding.right) / numericReadings.length - 10
                );
                const barHeight = chartHeight - (point.y - padding.top);
                const barX = point.x - barWidth / 2;

                return (
                  <rect
                    key={i}
                    x={barX}
                    y={point.y}
                    width={barWidth}
                    height={barHeight}
                    fill={
                      point.reading.status === 'critical'
                        ? '#ef4444'
                        : point.reading.status === 'warning'
                        ? '#f59e0b'
                        : '#3b82f6'
                    }
                    opacity="0.8"
                  >
                    <title>
                      {formatDate(point.reading.readingDate)}: {formatValue(point.value)}
                      {definition?.unit && ` ${definition.unit}`}
                    </title>
                  </rect>
                );
              })}
            </>
          )}

          {/* X-axis labels */}
          {dataPoints
            .filter((_, i) => i % Math.max(1, Math.floor(dataPoints.length / 6)) === 0)
            .map((point) => (
              <text
                key={point.index}
                x={point.x}
                y={padding.top + chartHeight + 20}
                textAnchor="middle"
                className="text-xs fill-gray-600"
              >
                {formatDate(point.reading.readingDate)}
              </text>
            ))}

          {/* Axis lines */}
          <line
            x1={padding.left}
            y1={padding.top + chartHeight}
            x2={chartWidth - padding.right}
            y2={padding.top + chartHeight}
            stroke="#9ca3af"
            strokeWidth="2"
          />
          <line
            x1={padding.left}
            y1={padding.top}
            x2={padding.left}
            y2={padding.top + chartHeight}
            stroke="#9ca3af"
            strokeWidth="2"
          />
        </svg>
      </div>

      {/* Statistics */}
      {trend && (
        <div className="grid grid-cols-5 gap-4">
          <div className="text-center">
            <div className="text-xs text-gray-600">Count</div>
            <div className="text-lg font-semibold text-gray-900">
              {trend.statistics.count}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-600">Average</div>
            <div className="text-lg font-semibold text-gray-900">
              {formatValue(trend.statistics.avg)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-600">Minimum</div>
            <div className="text-lg font-semibold text-gray-900">
              {formatValue(trend.statistics.min)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-600">Maximum</div>
            <div className="text-lg font-semibold text-gray-900">
              {formatValue(trend.statistics.max)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-600">Std Dev</div>
            <div className="text-lg font-semibold text-gray-900">
              {formatValue(trend.statistics.stdDev)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
